import React, { useState, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from "@mui/material";
import { CleaningServicesRounded } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import SidebarNavItem from "./SidebarNavItem";

/**
 * Vymaže lokálnu cache pamäť prehliadača pre túto aplikáciu:
 *  - Cache Storage API (PWA / fetch cache)
 *  - registrované service workery
 *  - sessionStorage
 *  - localStorage (vrátane prihlasovacích tokenov => odhlásenie)
 *  - IndexedDB databázy (best-effort)
 * Následne tvrdo presmeruje na prihlasovaciu stránku.
 */
const clearBrowserLocalCache = async () => {
  // 1) Cache Storage API
  try {
    if (typeof caches !== "undefined" && caches.keys) {
      const keys = await caches.keys();
      await Promise.all(keys.map((k) => caches.delete(k)));
    }
  } catch (e) {
    console.warn("Cache Storage clear failed", e);
  }

  // 2) Service workery
  try {
    if (navigator.serviceWorker && navigator.serviceWorker.getRegistrations) {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map((r) => r.unregister()));
    }
  } catch (e) {
    console.warn("Service worker unregister failed", e);
  }

  // 3) IndexedDB (best-effort; databases() nie je všade dostupné)
  try {
    if (window.indexedDB && indexedDB.databases) {
      const dbs = await indexedDB.databases();
      await Promise.all(
        (dbs || [])
          .map((d) => d && d.name)
          .filter(Boolean)
          .map(
            (name) =>
              new Promise((resolve) => {
                const req = indexedDB.deleteDatabase(name);
                req.onsuccess = req.onerror = req.onblocked = () => resolve();
              })
          )
      );
    }
  } catch (e) {
    console.warn("IndexedDB clear failed", e);
  }

  // 4) sessionStorage + localStorage (tokeny tu => používateľa to odhlási)
  try {
    sessionStorage.clear();
  } catch (e) {
    console.warn("sessionStorage clear failed", e);
  }
  try {
    localStorage.clear();
  } catch (e) {
    console.warn("localStorage clear failed", e);
  }
};

const ClearCacheItem = ({ open }) => {
  const { t } = useTranslation();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const handleConfirm = useCallback(async () => {
    setBusy(true);
    await clearBrowserLocalCache();
    // Tvrdé presmerovanie na prihlásenie (bez využitia react-routera,
    // aby sa znovu načítal celý JS/CSS bundle z čistého stavu).
    window.location.replace(
      `${process.env.PUBLIC_URL || ""}/login?cache_cleared=1`
    );
  }, []);

  return (
    <>
      <SidebarNavItem
        open={open}
        icon={<CleaningServicesRounded />}
        text={t("sidebar.items.clearCache", "Vymazať lokálnu cache")}
        active={false}
        onClick={() => setConfirmOpen(true)}
      />

      <Dialog
        open={confirmOpen}
        onClose={() => !busy && setConfirmOpen(false)}
        aria-labelledby="clear-cache-dialog-title"
      >
        <DialogTitle id="clear-cache-dialog-title">
          {t("sidebar.clearCache.title", "Vymazať lokálnu cache prehliadača?")}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t(
              "sidebar.clearCache.body",
              "Vymažú sa dočasné dáta aplikácie uložené v tomto prehliadači (cache, service workery a lokálne úložisko). Budete odhlásený a presmerovaný na prihlásenie. Nahrané a uložené súbory na serveri zostanú nedotknuté."
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)} disabled={busy}>
            {t("sidebar.clearCache.cancel", "Zrušiť")}
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirm}
            disabled={busy}
          >
            {busy
              ? t("sidebar.clearCache.clearing", "Mažem…")
              : t("sidebar.clearCache.confirm", "Vymazať a odhlásiť")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ClearCacheItem;
